/**
 * "(STUCK) Can't link benefits" (Gwynn, UMED operator, 2026-10-12, via Isaiah Cotto).
 *
 *   "I can create their DCF accounts using their email addresses, but after that I need to link their benefits. Some
 *   residents' benefits I'm unable to link because of incorrect information."
 *
 * The DCF account exists (DCF email created), but the resident's benefits cannot be linked to it, so the UMED cannot be
 * uploaded. A short reason is required; "Benefits linked" clears it. Who and when are the server's: the case's own
 * events (backend services/umed/dcfBenefits.js). Shown in the DCF account section of the case and, as a row panel, from
 * the board's status menu -- the same acts in both places. Finance sees it in its dossier.
 *
 * Every state is said in words, never by colour alone.
 */
import React, { useEffect, useRef, useState } from 'react';
import { umedApi, freshKey, shortDateTime, DCF_BENEFITS_STUCK_LABEL, purposeNeedsDcfAccount } from './umedApi';
import type { DcfBenefits, DcfAccountRecord } from './umedApi';

/** A board row's DCF account, as the read model sends it (never the secret). */
type BoardRowDcfAccount = { state: string; source: 'EMERGE' | 'MONDAY'; need?: string | null; email?: string | null; existing_case_id?: string | null; revealable?: boolean };

type Outcome = { success: boolean; error?: string; message?: string; code?: string; status?: number };
const said = (r: Outcome): string => {
  if (r.status === undefined) return 'Could not reach the server. Nothing was recorded.';
  if (r.status >= 500) return 'The server failed while recording this. Nothing was recorded, and it is not a permissions problem.';
  return r.error || r.message || (r.code ? `Refused (${r.code}).` : 'The server refused this.');
};

export const BENEFITS_STUCK_EFFECT = 'Marks the resident’s DCF benefits as impossible to link for now, with your reason. The case shows it on the board, in DCF Benefits Stuck and to Finance; the DCF upload cannot be confirmed until it is cleared. Downloading and faxing the packet are not held.';
export const BENEFITS_LINKED_EFFECT = 'Clears the stuck mark: the benefits are linked. The upload can then be confirmed.';

/*
 * STUCK = account made (ruling, Isaiah Cotto 2026-10-13). Benefits can only fail to link on a DCF account that exists,
 * so the stuck mark ALSO records the case's DCF account as created when it is not yet (the server does it in the same
 * transaction: nativeWorkflowService.markDcfBenefitsStuck). Gwynn marked a NEW UMED stuck without "DCF email created"
 * and every fax act stayed shut with no path forward; the form now says what the mark does to the account, and asks for
 * the email -- and the password -- only where the case has none on record. An email is never invented.
 */
/**
 * What the screen knows of the case's DCF account: this case's own record, whether the resident has one made on another
 * UMED, what the account is to this work (`need`), and what the case is sending now (`purpose`).
 */
export interface StuckAccountFacts {
  state?: string | null; source?: 'EMERGE' | 'MONDAY' | null; email?: string | null; has_credential?: boolean | null; existing?: boolean;
  need?: string | null; purpose?: string | null;
}
/*
 * Director's ruling within Isaiah Cotto's, 2026-10-13: STUCK records an account only where the work needs one
 * (purposeNeedsDcfAccount, umedApi) -- a correction or a reversal, or a case the board says needs none, is marked stuck
 * exactly as before, and nothing is asked.
 */
/** The facts from the case panel's account record (DcfAccountRecord), and what the case is sending. */
export const stuckFactsOfRecord = (a: DcfAccountRecord | null | undefined, purpose: string | null = null): StuckAccountFacts | null => (a ? {
  state: a.state, source: a.source, email: a.email, has_credential: a.has_credential, existing: Boolean(a.existing), need: a.need || null, purpose,
} : null);
/** The facts from a board row's account (`revealable` is whether a credential is on record; never the secret). */
export const stuckFactsOfRow = (a: BoardRowDcfAccount | null | undefined, purpose: string | null = null): StuckAccountFacts | null => (a ? {
  state: a.state, source: a.source, email: a.email || null, has_credential: Boolean(a.revealable), existing: Boolean(a.existing_case_id), need: a.need || null, purpose,
} : null);
/** What the stuck mark will do to the account, and what it must ask for. `records` false = it leaves the account as it is. */
export interface StuckAccountAsk { records: boolean; email: boolean; password: boolean; recordedEmail: string | null }
const NOTHING_TO_RECORD: StuckAccountAsk = { records: false, email: false, password: false, recordedEmail: null };
/** The server's rule (stuckAccountPlan), from the screen's facts. Unknown facts ask for nothing: the server says if it needs more. */
export function stuckAccountAsk(a: StuckAccountFacts | null | undefined): StuckAccountAsk {
  if (!a) return NOTHING_TO_RECORD;
  const native = a.source === 'MONDAY' ? null : (a.state || null);
  if (native === 'ACCOUNT_CREATED' || native === 'UNABLE_TO_CREATE') return NOTHING_TO_RECORD;
  // The work needs no account (a correction, a reversal): nothing recorded, nothing asked.
  if (!purposeNeedsDcfAccount(a.purpose)) return NOTHING_TO_RECORD;
  if (native === 'EMAIL_ASSIGNED') return { records: true, email: false, password: !a.has_credential, recordedEmail: a.email || null };
  // No account of this case's own: the resident's account made on another UMED is this work's; nothing to record.
  if (a.existing) return NOTHING_TO_RECORD;
  // The board says this work needs no account, and nothing native says otherwise.
  if (a.need === 'NOT_REQUIRED') return NOTHING_TO_RECORD;
  return { records: true, email: true, password: true, recordedEmail: null };
}
/** The plain words the stuck form says about the account, before it is marked. Null when the mark leaves the account alone. */
export function stuckAccountWords(ask: StuckAccountAsk): string | null {
  if (!ask.records) return null;
  const why = 'Benefits can only fail to link on a DCF account that exists, so marking this also records the resident’s DCF account as created';
  const opens = 'That opens Fax to DCF and Record fax sending; the DCF upload stays blocked until the benefits are linked.';
  if (ask.email) return `${why}. No DCF email is recorded for this resident: enter the email the account was made with, and its password. ${opens}`;
  const withEmail = ask.recordedEmail ? ` (${ask.recordedEmail})` : '';
  if (ask.password) return `${why}${withEmail}. No password is recorded for it: enter the one the account was made with. ${opens}`;
  return `${why}${withEmail}, keeping the password on record. ${opens}`;
}

/** One line for a row or a fact: the state in words, the reason, who and when. */
export function benefitsLine(b: DcfBenefits | null | undefined): string | null {
  if (!b) return null;
  const who = [b.recorded_by_name, b.recorded_at ? shortDateTime(b.recorded_at) : null].filter(Boolean).join(' ');
  if (b.state === 'STUCK') return `${DCF_BENEFITS_STUCK_LABEL}${b.reason ? `: ${b.reason}` : ''}${who ? ` (${who})` : ''}`;
  return `Benefits linked${b.note ? `: ${b.note}` : ''}${who ? ` (${who})` : ''}`;
}

const EMAIL_SHAPE = /^[^\s@]{1,128}@[^\s@]{1,128}\.[^\s@]{2,24}$/;

/**
 * The record form, for one act. `onClose` is Cancel (and after success, unless the caller keeps it). `account`: what the
 * screen knows of the case's DCF account, so the stuck form can say it records the account and ask for what it needs.
 */
export function BenefitsForm({ caseId, mode, onDone, onClose, account = null, focusFirst = true, onFocused }: {
  caseId: string; mode: 'stuck' | 'linked';
  /** Move focus to the first field on mount. A caller whose form can be re-mounted by a refresh passes true only for a user's open, and clears it in onFocused. */
  focusFirst?: boolean; onFocused?: () => void;
  onDone: (msg: string) => Promise<void> | void; onClose: () => void;
  account?: StuckAccountFacts | null;
}) {
  const [text, setText] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // What the server asked for that the screen did not know it would (DCF_EMAIL_REQUIRED / DCF_PASSWORD_REQUIRED).
  const [asked, setAsked] = useState<{ email: boolean; password: boolean }>({ email: false, password: false });
  const key = React.useRef(freshKey());
  // The form replaces the button that opened it: focus goes to its first field, so keyboard users are not dropped on the page.
  const firstField = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (!focusFirst || !firstField.current) return;
    firstField.current.focus({ preventScroll: true });
    if (onFocused) onFocused();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusFirst]);
  const base = stuckAccountAsk(account);
  const ask: StuckAccountAsk = mode !== 'stuck' ? NOTHING_TO_RECORD
    : (asked.email || asked.password) ? { records: true, email: base.email || asked.email, password: base.password || asked.password, recordedEmail: base.recordedEmail } : base;
  const emailOk = !ask.email || EMAIL_SHAPE.test(email.trim());
  const passwordOk = !ask.password || (password.length >= 6 && password.length <= 256);
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const r = mode === 'stuck'
        ? await umedApi.dcfBenefitsStuck(caseId, {
          reason: text.trim(), ...(ask.email ? { email: email.trim() } : {}), ...(ask.password ? { password } : {}), idempotency_key: key.current,
        })
        : await umedApi.dcfBenefitsCleared(caseId, { ...(text.trim() ? { note: text.trim() } : {}), idempotency_key: key.current });
      if (!r.success) {
        // The server knows the account better than the screen: show the field it asked for, with its words.
        if (r.code === 'DCF_EMAIL_REQUIRED') setAsked((a) => ({ ...a, email: true, password: true }));
        if (r.code === 'DCF_PASSWORD_REQUIRED') setAsked((a) => ({ ...a, password: true }));
        setErr(said(r)); return;
      }
      key.current = freshKey();
      setPassword('');
      onClose();
      const recorded = mode === 'stuck' && r.data && (r.data as { account?: { recorded?: boolean } }).account?.recorded === true;
      await onDone(mode === 'stuck'
        ? `Recorded: ${DCF_BENEFITS_STUCK_LABEL}.${recorded ? ' The resident’s DCF account is recorded as created, so the packet can be faxed (Fax to DCF or Record fax sending).' : ''} The DCF upload cannot be confirmed until it is cleared.`
        : 'Recorded: the DCF benefits are linked; the stuck mark is cleared.');
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  const label = mode === 'stuck' ? DCF_BENEFITS_STUCK_LABEL : 'Benefits linked';
  const accountWords = mode === 'stuck' ? stuckAccountWords(ask) : null;
  return (
    <div className="um-form um-rowpanel" role="group" aria-label={label}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={busy ? undefined : (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } }}>
      <p className="um-rowpanel-what">{mode === 'stuck' ? BENEFITS_STUCK_EFFECT : BENEFITS_LINKED_EFFECT}</p>
      {accountWords && <p className="um-rowpanel-what" data-testid="stuck-records-account">{accountWords}</p>}
      <label className="um-rowfield"><span>{mode === 'stuck' ? 'Why the benefits cannot be linked' : 'Note (optional)'}</span>
        <textarea ref={firstField} value={text} onChange={(e) => setText(e.target.value)} maxLength={500} rows={2} disabled={busy}
          placeholder={mode === 'stuck' ? 'What is wrong with the information at DCF' : 'Optional'}
          aria-label={mode === 'stuck' ? 'Why the benefits cannot be linked' : 'Note on linking the benefits (optional)'} /></label>
      {ask.email && (
        <label className="um-rowfield"><span>DCF account email (required)</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} required autoComplete="off"
            placeholder="The email the DCF account was made with" aria-label="DCF account email" /></label>
      )}
      {ask.password && (
        <label className="um-rowfield"><span>DCF account password (required)</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} required autoComplete="new-password"
            aria-label="DCF account password" /></label>
      )}
      {ask.password && <p className="um-quiet">Stored encrypted. Seen again only through an audited reveal.</p>}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
      <div className="btns um-rowpanel-acts">
        <button type="button" className="um-open um-primary" disabled={busy || (mode === 'stuck' && (text.trim().length < 3 || !emailOk || !passwordOk))} onClick={run}>
          {busy ? 'Recording…' : mode === 'stuck' ? (ask.records ? 'Record stuck and account created' : 'Record stuck') : 'Record benefits linked'}
        </button>
        <button type="button" className="um-link" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/** The DCF account section's Benefits line, with its one act (stuck, or linked when stuck). */
export default function DcfBenefitsLink({ caseId, benefits, canManage, closed = false, onDone, account = null }: {
  caseId: string; benefits: DcfBenefits | null | undefined; canManage: boolean; closed?: boolean; onDone: (msg: string) => Promise<void> | void;
  /** The case's DCF account, as the section shows it: the stuck form says it records it, and asks for what it needs (2026-10-13). */
  account?: StuckAccountFacts | null;
}) {
  const [mode, setMode] = useState<'none' | 'stuck' | 'linked'>('none');
  const stuck = benefits?.state === 'STUCK';
  return (
    <div className="um-benefits" data-testid="dcf-benefits">
      <dl className="um-kv">
        <dt>Benefits</dt>
        <dd className={stuck ? 'stop' : benefits ? '' : 'none'}>{benefitsLine(benefits) || 'Nothing recorded'}</dd>
      </dl>
      {canManage && !closed && mode === 'none' && (
        <div className="btns">
          {stuck
            ? <button type="button" className="um-open" onClick={() => setMode('linked')}>Benefits linked…</button>
            : <button type="button" className="um-open" onClick={() => setMode('stuck')}>{DCF_BENEFITS_STUCK_LABEL}…</button>}
        </div>
      )}
      {mode !== 'none' && <BenefitsForm caseId={caseId} mode={mode} onDone={onDone} onClose={() => setMode('none')} account={account} />}
    </div>
  );
}
